import { useEffect, useRef, useState } from 'react';
import { CATEGORY_META, categoryMeta } from '../lib/vehicleCategory';
import { leadsService } from '../lib/services';

/**
 * One-click New / Used / Demo reclassification on the board card. (E4 / T034,
 * Ajay #4 / A9.)
 *
 * Ricky's case is a customer who enquires on a new car and buys used. Until
 * now vehicle_category was written once by VehicleCategoryResolver at creation
 * and nothing could change it, so that sale was counted under New forever and
 * every split on every dashboard was quietly wrong.
 *
 * ── Why it stays a 3px stripe ──
 * T033 chose a stripe over a fifth chip because the card already carries score,
 * SLA, channel and next step, and one more badge costs more attention than the
 * fact is worth. That reasoning did not stop being true because the stripe
 * became clickable, so the resting appearance is unchanged — it grows a hover
 * affordance and a menu, nothing else. The acceptance criterion is literally
 * "the lead stays in the Lead Accelerator; only the colour changes".
 *
 * ── Why a menu and not a toggle ──
 * "One-click convert new→used" is the headline, but there are three categories,
 * and a click that cycles NEW→USED→DEMO→NEW makes the common case fast and the
 * uncommon case a guessing game. The menu is one click to open, one to choose,
 * and it can never land somewhere you did not intend.
 *
 * ── Unclassified is not offered ──
 * It is a UI-only bucket meaning "capture never told us". Letting someone
 * select it would make a real answer indistinguishable from a missing one, and
 * the Unclassified count is how a dealer principal finds broken capture.
 * Clearing a category is a data-repair job, not a board action.
 *
 * ── Optimistic, with a real revert ──
 * The point of the feature is that it feels like one click. It writes through
 * to the server and rolls the colour back if that fails, rather than leaving a
 * dealer believing a reclassification stuck when it did not.
 */
export default function CategoryStripe({ lead, onChanged }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);   // optimistic value
  const [error, setError] = useState(false);
  const ref = useRef(null);

  const value = pending || lead.vehicleCategory || '';
  const meta = categoryMeta(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = async (next) => {
    setOpen(false);
    if (next === value) return;
    const previous = lead.vehicleCategory || '';
    setPending(next);
    setError(false);
    try {
      await leadsService.updateVehicleCategory(lead.id, next);
      onChanged?.();                       // refreshLeads() — board + splits follow
    } catch (e) {
      // Put the colour back. A reclassification that silently did not happen is
      // worse than one that visibly failed: the dealer walks away believing the
      // report is now right.
      setPending(previous || null);
      setError(true);
      console.warn('[category] reclassify failed', e?.message);
      setTimeout(() => { setPending(null); setError(false); }, 2500);
    }
  };

  // Unclassified leads still need a target to click, or the one case that most
  // needs fixing is the one case with no affordance.
  const restColor = meta ? meta.color : 'var(--border-color)';

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); }}
        title={meta
          ? `${meta.label} vehicle — click to reclassify`
          : 'Not classified New or Used — click to set'}
        aria-label={meta ? `Vehicle category ${meta.label}` : 'Vehicle category not set'}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-block', width: 3, height: 11, padding: 0,
          marginRight: 6, verticalAlign: 'middle',
          border: 0, borderRadius: 2, cursor: 'pointer',
          background: error ? 'var(--danger)' : restColor,
          // The only resting change from T033's stripe: a slightly wider hit
          // area than 3px, achieved with an outline rather than size, so the
          // card layout does not move.
          outline: open ? `2px solid ${restColor}` : 'none',
          outlineOffset: 2,
          opacity: pending ? 0.55 : 1,
          transition: 'opacity 120ms ease',
        }}
      />
      {open && (
        <div
          role="menu"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          style={{
            position: 'absolute', top: 16, left: -6, zIndex: 40, minWidth: 108,
            background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
            borderRadius: 6, padding: 4, boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
          }}
        >
          {Object.entries(CATEGORY_META).map(([key, m]) => (
            <button
              key={key}
              role="menuitem"
              type="button"
              onClick={() => choose(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                padding: '5px 8px', border: 0, borderRadius: 4, cursor: 'pointer',
                background: key === value ? 'var(--bg-glass)' : 'transparent',
                color: 'var(--text-primary)', fontSize: 12, textAlign: 'left',
                fontWeight: key === value ? 600 : 400,
              }}
            >
              <span style={{ width: 3, height: 11, borderRadius: 2, background: m.color }} />
              {m.label}
              {key === value && <span style={{ marginLeft: 'auto', opacity: 0.6 }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
